// Admin "Origination queue" — review advisor-submitted curated deals.
// PRD: .local/prds/curated-deals-v1.md (§ 7)

import { useEffect, useMemo, useState } from "react";
import { Sparkles, Loader2, Check, X, AlertCircle, ChevronLeft, ListFilter, Pause, Play, Square, Users, MessageSquare, Shield, TrendingUp, AlertTriangle, Clock } from "lucide-react";

const BASE = import.meta.env.BASE_URL;
const headers = () => ({ "Content-Type": "application/json", "x-admin-token": localStorage.getItem("admin_token") || "" });
const inrShort = (n: number | null | undefined) => {
  if (n == null) return "—"; const x = Number(n);
  if (x >= 10000000) return `₹${(x / 10000000).toFixed(1)}Cr`;
  if (x >= 100000) return `₹${(x / 100000).toFixed(1)}L`;
  return `₹${x.toLocaleString("en-IN")}`;
};

const STATUS_TONE: Record<string, string> = {
  draft: "bg-white/[0.06] text-white/70 border-white/15",
  submitted: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  admin_review: "bg-blue-500/20 text-blue-200 border-blue-500/40",
  changes_requested: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  rejected: "bg-red-500/15 text-red-300 border-red-500/30",
  approved: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  live: "bg-emerald-500/20 text-emerald-200 border-emerald-500/40",
  paused: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  closed: "bg-white/[0.06] text-white/40 border-white/10",
};

export default function AdminOriginations() {
  const [deals, setDeals] = useState<any[]>([]);
  const [investors, setInvestors] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>("submitted");
  const [selected, setSelected] = useState<any | null>(null);
  const [actionMode, setActionMode] = useState<null | "approve" | "reject" | "request-changes" | "pause" | "close">(null);
  const [metrics, setMetrics] = useState<any | null>(null);
  const [showMetrics, setShowMetrics] = useState(false);

  const load = async () => {
    try {
      const r = await fetch(`${BASE}api/admin/curated-deals`, { headers: headers() });
      if (r.ok) setDeals(await r.json());
    } catch {}
    try {
      const ri = await fetch(`${BASE}api/admin/investors`, { headers: headers() });
      if (ri.ok) setInvestors(await ri.json());
    } catch {}
    try {
      const rm = await fetch(`${BASE}api/admin/curated-deals/metrics`, { headers: headers() });
      if (rm.ok) setMetrics(await rm.json());
    } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return deals;
    if (filter === "queue") return deals.filter(d => ["submitted", "admin_review", "changes_requested"].includes(d.status));
    if (filter === "live") return deals.filter(d => ["live", "paused"].includes(d.status));
    return deals.filter(d => d.status === filter);
  }, [deals, filter]);
  const queueCount = deals.filter(d => ["submitted", "admin_review"].includes(d.status)).length;

  return (
    <div className="bg-[#0A0A0A] border border-white/[0.08] rounded-2xl p-6 md:p-8 space-y-5 -mx-2 md:mx-0">
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500/30 to-blue-500/30 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-6 h-6 text-emerald-300" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-white">Origination queue</h1>
          <p className="text-white/55 text-sm mt-1">Advisor-submitted curated deals. Approve, reject, or request changes. Approved deals distribute immediately to the chosen audience.</p>
        </div>
      </div>

      {/* Drishti — distribution & conversion telemetry. Collapsed by default to
          keep the queue the focal point; expand for the funnel detail. */}
      {metrics && (metrics.totals.live + metrics.totals.paused + metrics.totals.closed) > 0 && (
        <div className="rounded-2xl border border-purple-500/20 bg-gradient-to-br from-purple-500/[0.06] to-blue-500/[0.04]">
          <button onClick={() => setShowMetrics(s => !s)} className="w-full flex items-center gap-3 p-4 text-left hover:bg-white/[0.02] rounded-2xl transition-colors">
            <TrendingUp className="w-5 h-5 text-purple-300 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-semibold">Drishti — distribution telemetry</p>
              <p className="text-white/55 text-xs mt-0.5">
                {metrics.totals.live} live · {metrics.totals.paused} paused · {metrics.totals.closed} closed
                {metrics.totals.underperforming > 0 && <span className="text-amber-300"> · {metrics.totals.underperforming} underperforming</span>}
                {metrics.totals.expiringSoon > 0 && <span className="text-amber-300"> · {metrics.totals.expiringSoon} expiring</span>}
              </p>
            </div>
            <ChevronLeft className={`w-4 h-4 text-white/40 transition-transform ${showMetrics ? "-rotate-90" : "rotate-180"}`} />
          </button>
          {showMetrics && (
            <div className="px-4 pb-4 space-y-2">
              {metrics.perDeal.length === 0 ? (
                <p className="text-white/55 text-xs">No live or closed deals yet.</p>
              ) : metrics.perDeal.map((m: any) => (
                <div key={m.id} className="rounded-xl border border-white/[0.08] bg-black/30 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="text-white text-sm font-semibold truncate">{m.title}</h4>
                        <span className={`text-[9px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded border ${STATUS_TONE[m.status]}`}>{m.status}</span>
                        {m.flags.underperforming && (
                          <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded border bg-amber-500/15 text-amber-300 border-amber-500/30">
                            <AlertTriangle className="w-2.5 h-2.5" /> underperforming
                          </span>
                        )}
                        {m.flags.expiringSoon && (
                          <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded border bg-orange-500/15 text-orange-300 border-orange-500/30">
                            <Clock className="w-2.5 h-2.5" /> expiring {m.daysToClose}d
                          </span>
                        )}
                      </div>
                      <p className="text-white/45 text-[11px] mt-1">{m.dealType} · live {m.ageDays}d · {inrShort(m.committedInr)} committed{m.indicativeInr ? ` (+${inrShort(m.indicativeInr)} indicative)` : ""}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-5 gap-1.5 mt-2.5 text-center">
                    {[
                      { l: "Sent", v: m.funnel.distributed },
                      { l: "Viewed", v: m.funnel.viewed, sub: m.rates.viewRate ? `${m.rates.viewRate}%` : null },
                      { l: "Interested", v: m.funnel.interested, sub: m.rates.interestOfViewers ? `${m.rates.interestOfViewers}%` : null },
                      { l: "Meeting", v: m.funnel.meetings },
                      { l: "Committed", v: m.funnel.committed, sub: m.rates.commitOfInterested ? `${m.rates.commitOfInterested}%` : null },
                    ].map(s => (
                      <div key={s.l} className="rounded-lg bg-white/[0.04] border border-white/[0.06] p-1.5">
                        <p className="text-white text-sm font-mono font-semibold">{s.v}</p>
                        <p className="text-white/45 text-[9px] uppercase tracking-wider font-mono">{s.l}</p>
                        {s.sub && <p className="text-purple-300/70 text-[9px] font-mono mt-0.5">{s.sub}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {[
          { k: "queue", label: `In queue ${queueCount > 0 ? `(${queueCount})` : ""}`, hot: queueCount > 0 },
          { k: "live", label: "Live + paused" },
          { k: "approved", label: "Approved (waiting)" },
          { k: "rejected", label: "Rejected" },
          { k: "all", label: "All" },
        ].map(t => (
          <button key={t.k} onClick={() => setFilter(t.k)} className={`text-left rounded-xl border p-3.5 transition-all ${filter === t.k ? "ring-2 ring-[#FF5C00]/40 border-[#FF5C00]/40" : "border-white/[0.08] hover:border-white/[0.2]"} ${t.hot && filter !== t.k ? "bg-blue-500/10" : "bg-white/[0.04]"}`}>
            <p className="text-[10px] uppercase tracking-wider text-white/55 font-mono">{t.label}</p>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* List */}
        <div className="lg:col-span-1 space-y-2 max-h-[700px] overflow-y-auto pr-1">
          {loading ? [...Array(3)].map((_, i) => <div key={i} className="h-24 bg-white/[0.04] rounded-xl animate-pulse" />) :
           filtered.length === 0 ? (
            <div className="bg-white/[0.04] border border-white/[0.1] rounded-xl p-8 text-center">
              <ListFilter className="w-8 h-8 text-white/20 mx-auto mb-2" />
              <p className="text-white/55 text-sm">Nothing here.</p>
            </div>
          ) : filtered.map(d => (
            <button key={d.id} onClick={() => { setSelected(d); setActionMode(null); }} className={`w-full text-left rounded-xl border p-3 transition-all ${selected?.id === d.id ? "ring-2 ring-[#FF5C00]/40 border-[#FF5C00]/40" : "border-white/[0.08] hover:border-white/[0.18]"} bg-white/[0.04]`}>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-white text-sm font-semibold truncate flex-1 min-w-0">{d.title}</h3>
                <span className={`text-[9px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded border ${STATUS_TONE[d.status]}`}>{d.status.replace(/_/g, " ")}</span>
              </div>
              <p className="text-white/55 text-xs mt-1">by {d.advisor?.name || "?"} · {d.dealType} · {inrShort(d.raiseAmountInr)}</p>
              {d.submittedAt && (
                <p className="text-[10px] text-white/40 mt-1">Submitted {new Date(d.submittedAt).toLocaleString()}</p>
              )}
            </button>
          ))}
        </div>

        {/* Detail / actions */}
        <div className="lg:col-span-2">
          {!selected ? (
            <div className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-10 text-center">
              <Sparkles className="w-10 h-10 text-white/20 mx-auto mb-2" />
              <p className="text-white/55 text-sm">Select a deal to review.</p>
            </div>
          ) : (
            <DealReviewPanel deal={selected} investors={investors} actionMode={actionMode} setActionMode={setActionMode} onUpdated={async () => { await load(); const fresh = (await (await fetch(`${BASE}api/admin/curated-deals`, { headers: headers() })).json()).find((x: any) => x.id === selected.id); setSelected(fresh || null); }} />
          )}
        </div>
      </div>
    </div>
  );
}

function DealReviewPanel({ deal, investors, actionMode, setActionMode, onUpdated }: any) {
  const [editedSummary, setEditedSummary] = useState(deal.summary);
  const [scope, setScope] = useState("investor_list");
  const [includeIds, setIncludeIds] = useState<number[]>([]);
  const [riskFlags, setRiskFlags] = useState<any[]>([]);
  const [newFlagLabel, setNewFlagLabel] = useState("");
  const [newFlagSeverity, setNewFlagSeverity] = useState("info");
  const [rejectReason, setRejectReason] = useState("");
  const [changeNotes, setChangeNotes] = useState<{ field: string; note: string }[]>([{ field: "", note: "" }]);
  const [pauseReason, setPauseReason] = useState("");
  const [closeReason, setCloseReason] = useState("subscribed");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Reset state when deal changes
  useEffect(() => {
    setEditedSummary(deal.summary); setScope("investor_list"); setIncludeIds([]); setRiskFlags(Array.isArray(deal.riskFlags) ? deal.riskFlags : []);
    setRejectReason(""); setChangeNotes([{ field: "", note: "" }]); setPauseReason(""); setErr(null);
  }, [deal.id]);

  const approve = async () => {
    setBusy(true); setErr(null);
    try {
      const body: any = { scope, summary: editedSummary, riskFlags };
      if (scope === "investor_list") body.includeInvestorIds = includeIds;
      const r = await fetch(`${BASE}api/admin/curated-deals/${deal.id}/approve`, { method: "POST", headers: headers(), body: JSON.stringify(body) });
      if (!r.ok) { const e = await r.json(); setErr(e.error || "Approval failed."); }
      else { setActionMode(null); onUpdated(); }
    } catch (e: any) { setErr(e?.message); }
    setBusy(false);
  };
  const reject = async () => {
    if (rejectReason.length < 40) { setErr("Reason must be at least 40 characters."); return; }
    setBusy(true); setErr(null);
    try {
      const r = await fetch(`${BASE}api/admin/curated-deals/${deal.id}/reject`, { method: "POST", headers: headers(), body: JSON.stringify({ reason: rejectReason }) });
      if (!r.ok) { const e = await r.json(); setErr(e.error || "Reject failed."); }
      else { setActionMode(null); onUpdated(); }
    } catch (e: any) { setErr(e?.message); }
    setBusy(false);
  };
  const requestChanges = async () => {
    const valid = changeNotes.filter(n => n.field.trim() && n.note.trim());
    if (valid.length === 0) { setErr("Add at least one note."); return; }
    setBusy(true); setErr(null);
    try {
      const r = await fetch(`${BASE}api/admin/curated-deals/${deal.id}/request-changes`, { method: "POST", headers: headers(), body: JSON.stringify({ notes: valid }) });
      if (!r.ok) { const e = await r.json(); setErr(e.error || "Failed."); }
      else { setActionMode(null); onUpdated(); }
    } catch (e: any) { setErr(e?.message); }
    setBusy(false);
  };
  const pause = async () => {
    setBusy(true);
    await fetch(`${BASE}api/admin/curated-deals/${deal.id}/pause`, { method: "POST", headers: headers(), body: JSON.stringify({ reason: pauseReason }) });
    setActionMode(null); setBusy(false); onUpdated();
  };
  const resume = async () => {
    setBusy(true);
    await fetch(`${BASE}api/admin/curated-deals/${deal.id}/resume`, { method: "POST", headers: headers() });
    setBusy(false); onUpdated();
  };
  const close = async () => {
    setBusy(true);
    await fetch(`${BASE}api/admin/curated-deals/${deal.id}/close`, { method: "POST", headers: headers(), body: JSON.stringify({ closeReason }) });
    setActionMode(null); setBusy(false); onUpdated();
  };

  return (
    <div className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-5 space-y-4 max-h-[700px] overflow-y-auto">
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="text-xl font-bold text-white">{deal.title}</h2>
          <span className={`text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded border ${STATUS_TONE[deal.status]}`}>{deal.status.replace(/_/g, " ")}</span>
        </div>
        <p className="text-white/55 text-sm mt-1">{deal.companyName} · {deal.dealType} · {deal.instrumentType} · by {deal.advisor?.name}</p>
      </div>

      {err && <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2.5 text-xs text-red-200 flex items-start gap-2"><AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />{err}</div>}

      <div className="grid grid-cols-2 gap-2 text-sm">
        <div className="bg-black/30 rounded p-2"><p className="text-[10px] uppercase tracking-wider text-white/50 font-mono">Raise</p><p className="text-white">{inrShort(deal.raiseAmountInr)}</p></div>
        <div className="bg-black/30 rounded p-2"><p className="text-[10px] uppercase tracking-wider text-white/50 font-mono">Min ticket</p><p className="text-white">{inrShort(deal.minTicketInr)}</p></div>
        <div className="bg-black/30 rounded p-2"><p className="text-[10px] uppercase tracking-wider text-white/50 font-mono">{deal.dealType === "primary" ? "Pre-money" : "Price/share"}</p><p className="text-white">{inrShort(deal.preMoneyValInr || deal.pricePerShareInr)}</p></div>
        <div className="bg-black/30 rounded p-2"><p className="text-[10px] uppercase tracking-wider text-white/50 font-mono">Close by</p><p className="text-white">{deal.closeBy ? new Date(deal.closeBy).toLocaleDateString() : "—"}</p></div>
      </div>

      <div>
        <p className="text-[10px] uppercase tracking-wider text-white/55 font-mono mb-1">Investor-facing summary {(deal.status === "submitted" || deal.status === "admin_review") && "(editable)"}</p>
        {(deal.status === "submitted" || deal.status === "admin_review") ? (
          <textarea value={editedSummary} onChange={e => setEditedSummary(e.target.value)} maxLength={1200} rows={5} className="w-full bg-white/[0.04] border border-white/15 rounded p-2 text-sm text-white" />
        ) : (
          <p className="text-sm text-white/85 whitespace-pre-wrap bg-black/30 p-3 rounded">{deal.summary}</p>
        )}
      </div>

      {deal.thesis && (
        <div>
          <p className="text-[10px] uppercase tracking-wider text-white/55 font-mono mb-1">Advisor's private thesis</p>
          <p className="text-xs text-white/70 italic bg-black/30 p-2 rounded">{deal.thesis}</p>
        </div>
      )}

      {deal.feeStructureJson && (
        <div className="text-xs text-white/65">
          Fees: sourcing {deal.feeStructureJson.sourcingPct}% · success {deal.feeStructureJson.successPct}% · platform clip {deal.feeStructureJson.platformClipPct}%
        </div>
      )}

      {/* Action buttons by status */}
      {(deal.status === "submitted" || deal.status === "admin_review") && actionMode == null && (
        <div className="flex flex-wrap gap-2 pt-2 border-t border-white/10">
          <button onClick={() => setActionMode("approve")} className="bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/40 text-emerald-200 px-3 py-1.5 rounded text-sm flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Approve</button>
          <button onClick={() => setActionMode("request-changes")} className="bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 text-amber-200 px-3 py-1.5 rounded text-sm flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> Request changes</button>
          <button onClick={() => setActionMode("reject")} className="bg-red-500/20 hover:bg-red-500/30 border border-red-500/40 text-red-200 px-3 py-1.5 rounded text-sm flex items-center gap-1"><X className="w-3.5 h-3.5" /> Reject</button>
        </div>
      )}
      {deal.status === "live" && actionMode == null && (
        <div className="flex flex-wrap gap-2 pt-2 border-t border-white/10">
          <button onClick={() => setActionMode("pause")} className="bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 text-amber-200 px-3 py-1.5 rounded text-sm flex items-center gap-1"><Pause className="w-3.5 h-3.5" /> Pause</button>
          <button onClick={() => setActionMode("close")} className="bg-white/10 hover:bg-white/15 border border-white/15 text-white/80 px-3 py-1.5 rounded text-sm flex items-center gap-1"><Square className="w-3.5 h-3.5" /> Close</button>
        </div>
      )}
      {deal.status === "paused" && actionMode == null && (
        <div className="flex flex-wrap gap-2 pt-2 border-t border-white/10">
          <button onClick={resume} disabled={busy} className="bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/40 text-emerald-200 px-3 py-1.5 rounded text-sm flex items-center gap-1">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} Resume</button>
          <button onClick={() => setActionMode("close")} className="bg-white/10 hover:bg-white/15 border border-white/15 text-white/80 px-3 py-1.5 rounded text-sm flex items-center gap-1"><Square className="w-3.5 h-3.5" /> Close</button>
        </div>
      )}

      {/* Action UIs */}
      {actionMode === "approve" && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 space-y-3">
          <p className="text-sm font-semibold text-emerald-200">Approve & distribute</p>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-white/55 font-mono mb-1 flex items-center gap-1"><Shield className="w-3 h-3" /> Risk flags</p>
            <div className="space-y-1 mb-2">
              {riskFlags.map((f, i) => (
                <div key={i} className="flex items-center gap-2 bg-white/[0.04] rounded px-2 py-1">
                  <span className={`text-[10px] uppercase font-mono px-1.5 py-0.5 rounded ${f.severity === "material" ? "bg-red-500/20 text-red-200" : f.severity === "caution" ? "bg-amber-500/20 text-amber-200" : "bg-white/10 text-white/70"}`}>{f.severity}</span>
                  <span className="text-xs text-white/85 flex-1">{f.label}</span>
                  <button onClick={() => setRiskFlags(rs => rs.filter((_, j) => j !== i))} className="text-white/40 hover:text-red-300"><X className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
            <div className="flex gap-1">
              <select value={newFlagSeverity} onChange={e => setNewFlagSeverity(e.target.value)} className="bg-white/[0.04] border border-white/15 rounded px-2 py-1 text-xs text-white">
                <option value="info">info</option><option value="caution">caution</option><option value="material">material</option>
              </select>
              <input value={newFlagLabel} onChange={e => setNewFlagLabel(e.target.value)} placeholder="Risk description" className="flex-1 bg-white/[0.04] border border-white/15 rounded px-2 py-1 text-xs text-white" />
              <button onClick={() => { if (newFlagLabel.trim()) { setRiskFlags(rs => [...rs, { label: newFlagLabel.trim(), severity: newFlagSeverity }]); setNewFlagLabel(""); } }} className="text-xs bg-white/10 hover:bg-white/20 text-white px-2 py-1 rounded">+ Add</button>
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-white/55 font-mono mb-1">Distribution scope</p>
            <select value={scope} onChange={e => setScope(e.target.value)} className="w-full bg-white/[0.04] border border-white/15 rounded px-2 py-1.5 text-xs text-white mb-2">
              <option value="investor_list">Investor list (cap 50)</option>
              <option value="all_in_cohort" disabled>All in cohort (v2)</option>
              <option value="cohort_subset" disabled>Cohort subset (v2)</option>
            </select>
            {scope === "investor_list" && (
              <div className="max-h-40 overflow-y-auto border border-white/10 rounded p-2 space-y-1 bg-black/20">
                {investors.length === 0 && <p className="text-xs text-white/40">Loading investors…</p>}
                {investors.map((inv: any) => (
                  <label key={inv.id} className="flex items-center gap-2 text-xs text-white/85 cursor-pointer hover:bg-white/[0.04] px-1 py-0.5 rounded">
                    <input type="checkbox" checked={includeIds.includes(inv.id)} onChange={e => setIncludeIds(ids => e.target.checked ? [...ids, inv.id] : ids.filter(x => x !== inv.id))} />
                    <span>{inv.name}</span><span className="text-white/40">{inv.email}</span>
                  </label>
                ))}
              </div>
            )}
            <p className="text-[10px] text-white/40 mt-1"><Users className="w-3 h-3 inline mr-1" />{includeIds.length} selected · go-live: now</p>
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setActionMode(null)} className="text-xs text-white/55 hover:text-white px-2">Cancel</button>
            <button onClick={approve} disabled={busy || (scope === "investor_list" && includeIds.length === 0)} className="bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white text-sm px-4 py-1.5 rounded flex items-center gap-1">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Approve & distribute
            </button>
          </div>
        </div>
      )}
      {actionMode === "reject" && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 space-y-2">
          <p className="text-sm font-semibold text-red-200">Reject</p>
          <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Explain why (≥ 40 characters). The advisor sees this." rows={4} className="w-full bg-white/[0.04] border border-white/15 rounded p-2 text-sm text-white" />
          <p className="text-xs text-white/55">{rejectReason.length} / 40 minimum</p>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setActionMode(null)} className="text-xs text-white/55 hover:text-white px-2">Cancel</button>
            <button onClick={reject} disabled={busy || rejectReason.length < 40} className="bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white text-sm px-4 py-1.5 rounded">Reject</button>
          </div>
        </div>
      )}
      {actionMode === "request-changes" && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 space-y-2">
          <p className="text-sm font-semibold text-amber-200">Request changes</p>
          {changeNotes.map((n, i) => (
            <div key={i} className="flex gap-1">
              <input value={n.field} onChange={e => setChangeNotes(ns => ns.map((x, j) => j === i ? { ...x, field: e.target.value } : x))} placeholder="Field (e.g. summary)" className="w-32 bg-white/[0.04] border border-white/15 rounded px-2 py-1 text-xs text-white" />
              <input value={n.note} onChange={e => setChangeNotes(ns => ns.map((x, j) => j === i ? { ...x, note: e.target.value } : x))} placeholder="What to change" className="flex-1 bg-white/[0.04] border border-white/15 rounded px-2 py-1 text-xs text-white" />
            </div>
          ))}
          <button onClick={() => setChangeNotes(ns => [...ns, { field: "", note: "" }])} className="text-xs text-amber-200 hover:text-amber-100">+ Add another</button>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setActionMode(null)} className="text-xs text-white/55 hover:text-white px-2">Cancel</button>
            <button onClick={requestChanges} disabled={busy} className="bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-sm px-4 py-1.5 rounded">Send back</button>
          </div>
        </div>
      )}
      {actionMode === "pause" && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 space-y-2">
          <p className="text-sm font-semibold text-amber-200">Pause</p>
          <input value={pauseReason} onChange={e => setPauseReason(e.target.value)} placeholder="Reason (shown to investors)" className="w-full bg-white/[0.04] border border-white/15 rounded px-2 py-1.5 text-sm text-white" />
          <div className="flex gap-2 justify-end">
            <button onClick={() => setActionMode(null)} className="text-xs text-white/55 hover:text-white px-2">Cancel</button>
            <button onClick={pause} disabled={busy} className="bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-sm px-4 py-1.5 rounded">Pause</button>
          </div>
        </div>
      )}
      {actionMode === "close" && (
        <div className="bg-white/[0.04] border border-white/15 rounded-lg p-3 space-y-2">
          <p className="text-sm font-semibold text-white">Close deal</p>
          <select value={closeReason} onChange={e => setCloseReason(e.target.value)} className="w-full bg-white/[0.04] border border-white/15 rounded px-2 py-1.5 text-sm text-white">
            <option value="subscribed">Subscribed (marks fees as realized)</option>
            <option value="expired">Expired</option>
            <option value="killed">Killed</option>
          </select>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setActionMode(null)} className="text-xs text-white/55 hover:text-white px-2">Cancel</button>
            <button onClick={close} disabled={busy} className="bg-white/15 hover:bg-white/20 text-white text-sm px-4 py-1.5 rounded">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
